import axios from 'axios';
import dayjs from 'dayjs';
import React, { useState, useEffect, useRef } from 'react';
import Modal from 'react-bootstrap/Modal';
import { BsShieldExclamation } from 'react-icons/bs';
import { v4 as uuidv4 } from 'uuid';

import BayFileUpload from '../BayFilesUpload';
import { FALLBACK_THUMBNAIL } from '../utilities/constants/filesConstants';
import {
	mimeTypes,
	getAllowedFileTypes,
} from '../utilities/constants/mimeTypes';
import { getFileType } from '../utilities/fileExtensionHandler';
import {
	regexHasForbiddenCharTest,
	regexIsValidPathTest,
} from '../utilities/regex';
import {
	getVideoMetadata,
	isVideoFile,
	captureVideoThumbnail,
	isDemuxerError,
} from '../utilities/videoHelpers';

import CancelAllWarningModal from './components/CancelAllWarningModal';
import UploadModalFooter from './components/UploadModalFooter';
import UploadProgressBar from './components/UploadProgressBar';

import './BayFilesProgressModal.scss';

const regexHasForbiddenChar = new RegExp(regexHasForbiddenCharTest);
const regexIsValidPath = new RegExp(regexIsValidPathTest);

const VIDEO_MIN_DURATION_SEC = 3;
const VIDEO_MAX_DURATION_SEC = 301;
const VIDEO_MIN_ASPECT_RATIO = 0.01;
const VIDEO_MAX_ASPECT_RATIO = 10;
const VIDEO_MAX_SIZE_BYTES = 524288000;

const getPreventRetryErrors = (enforceVideoConstraints: boolean) => {
	return [
		'Video file has an unsupported codec',
		...(enforceVideoConstraints
			? [
					'Video file must be between 3 seconds and 5 minutes',
					'Video file must be between 1:1 and 16:9 aspect ratio',
				]
			: []),
		'Video file must not exceed 500MB',
		'file must not exceed',
		'File name must be a minimum of 3 characters',
		'File name must be a minimum of 3 characters and not exceed 64 characters',
		'File name contains characters that are not allowed',
		'File name contains extra spaces that are not allowed',
		'Unsupported video format or codec',
		'DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
	];
};

interface BayFilesProgressModalProps {
	t?: (key: string) => string;
	showUploadingFilesModal: boolean;
	handleModalClose: (params: { show: boolean; reload?: boolean }) => void;
	uploadingFiles?: File[];
	apiConfig: any;
	allowedFileTypesText?: string;
	allowedFileTypes?: any;
	formDataObjectName?: string;
	setApiRes?: (data: any) => void;
	allowedFileTypesErrorText?: string;
	allowedFileSizeErrorText?: string;
	maxFileSize?: number;
	maxFileSizeLabel?: number;
	uploadLinkText?: string;
	allowedFileTypeslabel?: string;
	fileUploaderStatus?: (index: number, status: string) => void;
	thumbnailFiles?: any[];
	shouldGenerateThumbnails?: boolean;
	isBulkUpload?: boolean;
	bulkDocumentDetails?: any[];
	enforceVideoConstraints?: boolean;
	isListingBeta?: boolean;
	allowedFileUpload?: boolean;
	cloudIconSize?: number;
	isImageUploader?: boolean;
	chunkSize?: number;
	onFileCancelled?: () => void;
	onCancelledUpload?: (data: any) => void;
	isUploadSection?: boolean;
	designId?: string | number | null;
	isFromEsign?: boolean;
	/**
	 * Copyright/permission disclaimer shown below the dropzone in the upload
	 * state. Off by default (opt-in): pass `true` for the standard disclaimer,
	 * or a node to override the message. Falsy renders nothing.
	 */
	copyrightDisclaimer?: boolean | React.ReactNode;
}

const BayFilesProgressModal: React.FC<BayFilesProgressModalProps> = ({
	t = (text) => text,
	showUploadingFilesModal,
	handleModalClose,
	uploadingFiles = [],
	apiConfig,
	allowedFileTypesText,
	allowedFileTypes = [],
	formDataObjectName = 'files[]',
	setApiRes = () => {},
	allowedFileTypesErrorText = `${t('defaultFileTypesErrorText')}`,
	allowedFileSizeErrorText,
	maxFileSize = 15000000,
	maxFileSizeLabel = 15,
	uploadLinkText,
	allowedFileTypeslabel,
	fileUploaderStatus = () => {},
	thumbnailFiles,
	shouldGenerateThumbnails = false,
	isBulkUpload = false,
	bulkDocumentDetails = [],
	enforceVideoConstraints = false,
	isListingBeta = false,
	allowedFileUpload = true,
	cloudIconSize = 40,
	isImageUploader = false,
	chunkSize = 0,
	onFileCancelled = () => {},
	onCancelledUpload = () => {},
	isUploadSection = false,
	designId = null,
	isFromEsign = false,
	copyrightDisclaimer = false,
}) => {
	const [progressBarFiles, setProgressBarFiles] = useState<any[]>([]);

	const [fileThumbnails, setFileThumbnails] = useState<any[]>([]);
	const modalFooterRef = useRef<any>();
	const cancelAllModalRef = useRef<any>();

	const [fileStatuses, setFileStatuses] = useState<any>({});

	const modalHelper = useRef<any>({
		fileProgressHelper: {},
		files: [],
	});

	useEffect(() => {
		if ((uploadingFiles?.length && progressBarFiles.length === 0) ||
			(isBulkUpload && bulkDocumentDetails?.length > 0 && progressBarFiles.length === 0)) {
			onDrop(uploadingFiles, shouldGenerateThumbnails);
		}
	}, [uploadingFiles?.length, isBulkUpload, bulkDocumentDetails?.length, progressBarFiles.length]);

	useEffect(() => {
		return () => {
			if (modalHelper.current.fileProgressHelper) {
				Object.keys(modalHelper.current.fileProgressHelper).forEach(index => {
					const helper = modalHelper.current.fileProgressHelper[index];
					if (helper && helper.controller) {
						try {
							helper.controller.abort();
						} catch (e) {
							// Ignore
						}
					}
				});
			}
		};
	}, []);

	const getSignal = (index: number) => {
		return modalHelper.current.fileProgressHelper[index].controller.signal;
	};

	const setController = (index: number) => {
		const controller = new AbortController();

		if (modalHelper.current.fileProgressHelper[index]) {
			modalHelper.current.fileProgressHelper[index].controller = controller;
		} else {
			modalHelper.current.fileProgressHelper[index] = {
				progress: 0,
				controller,
				status: 'progress',
				startAt: Date.now(),
				cancelled: false,
			};
		}

		return controller;
	};

	const abortController = (index: number) => {
		const file = modalHelper.current.fileProgressHelper[index];
		if (file && file.controller) {
			file.cancelled = true;
			file.status = 'cancel';

			try {
				if (file.controller.signal && !file.controller.signal.aborted) {
					file.controller.abort();
				}
			} catch (e) {
				console.error('Error aborting controller:', e);
			}

			updateFileStatus(index, {
				statusType: 'cancel',
				progressValue: 0,
				errorMessage: 'File upload was cancelled. Would you like to try again?',
			});

			setFileUploadingStatus(index, 'cancel');

			if (modalFooterRef.current) {
				modalFooterRef.current.setProcessing();
			}
		}
	};

	const setFileUploadingStatus = (index: number, status: string) => {
		const fileProgressHelper = modalHelper.current.fileProgressHelper;
		// Guard: the modal may have already been closed and the helper map reset;
		// in-flight rejections from aborted requests must not crash here.
		if (!fileProgressHelper[index]) return;
		fileProgressHelper[index].status = status;
		const allFiles = Object.keys(fileProgressHelper).filter(
			(key) => fileProgressHelper[key].status === 'success',
		);
		if (allFiles.length === Object.keys(fileProgressHelper).length) {
			setTimeout(() => {
				closeUploadingModal(true);
			}, 500);
			fileUploaderStatus(index, status);
		}
	};

	const updateFileStatus = (
		index: number,
		{ progressValue, statusType, errorMessage }: any,
	) => {
		// Guard: if the helper entry has been removed (modal closed and map reset),
		// skip both the ref update and the React state update to avoid crashes and
		// stale-state writes from in-flight abort rejections.
		if (!modalHelper.current.fileProgressHelper[index]) return;

		// Update the reference first for tracking
		if (modalHelper.current.fileProgressHelper[index]) {
			if (progressValue !== undefined) {
				modalHelper.current.fileProgressHelper[index].progress = progressValue;
			}
			if (statusType !== undefined) {
				modalHelper.current.fileProgressHelper[index].status = statusType;
			}
			if (errorMessage !== undefined) {
				modalHelper.current.fileProgressHelper[index].errorMessage =
					errorMessage;
			}
		}

		const updates: any = {};

		if (progressValue !== undefined) {
			const prevProgress = fileStatuses[index]?.progressValue || 0;

			if (
				statusType !== 'progress' ||
				Math.abs(progressValue - prevProgress) >= 10 ||
				progressValue === 100 ||
				prevProgress === 0
			) {
				updates.progressValue = progressValue;
			}
		}

		if (statusType !== undefined) {
			updates.statusType = statusType;
		}
		if (errorMessage !== undefined) {
			updates.errorMessage = errorMessage;
		}

		if (Object.keys(updates).length > 0) {
			setFileStatuses((prevStatuses: any) => ({
				...prevStatuses,
				[index]: {
					...prevStatuses[index],
					...updates,
				},
			}));
		}
	};

	const onDrop = (
		acceptedFiles: File[],
		generateThumbnails = shouldGenerateThumbnails,
	) => {
		try {
			if (isBulkUpload && bulkDocumentDetails?.length > 0) {
				setFilesForUpload([], generateThumbnails);
				return;
			}

			const fileName: string[] = [];
			let fileExtension;
			let mimeTypeKey = null;

			const fileNameCounts: Record<string, number> = {};

			const updatedFiles = acceptedFiles.map((data, index) => {
				fileExtension = data.name.substring(data.name.lastIndexOf('.') + 1);
				Object.keys(mimeTypes).map((key) => {
					if ((mimeTypes as any)[key] === fileExtension) {
						mimeTypeKey = key;
					}
				});
				const fileType = data.type === '' ? mimeTypeKey : data.type;

				let cleanedFileName = data.name
					.substring(0, data.name.lastIndexOf('.'))
					.replace(/[^\w ]/g, '')
					.replace(/\s{2,}/g, ' ')
					.trim();

				if (cleanedFileName.length > 64) {
					console.log(
						`Truncating file name - original: ${cleanedFileName.length} chars`,
					);
					cleanedFileName = cleanedFileName.substring(0, 64);
					console.log(`Truncated to: ${cleanedFileName.length} chars`);
				}

				if (fileNameCounts[cleanedFileName] === undefined) {
					fileNameCounts[cleanedFileName] = 0;
					fileName.push(cleanedFileName);
				} else {
					fileNameCounts[cleanedFileName]++;
					const newName = `${cleanedFileName}-${fileNameCounts[cleanedFileName]}`;
					fileName.push(newName);
				}

				return new File(
					[data],
					`${fileName[index]}.${data.name.substring(data.name.lastIndexOf('.') + 1, data.name.length)}`,
					{
						type: fileType || data.type,
						path: (data as any).path,
					},
				);
			});
			setFilesForUpload(updatedFiles, generateThumbnails);

		} catch (e) {
			console.error('Error processing files:', e);
		}
	};

	const showUploadingError = (index: number, error: string) => {
		const errorMsg =
			error || 'There was an error uploading your file. Please try again.';

		updateFileStatus(index, {
			progressValue: 100,
			statusType: 'invalid',
			errorMessage: errorMsg,
		});
		setFileUploadingStatus(index, 'invalid');

		if (modalHelper.current.fileProgressHelper[index]) {
			modalHelper.current.fileProgressHelper[index].validationError = errorMsg;
		}

		return;
	};

	async function createFiles({
		formData,
		index,
		isVideoFile,
		isAllowedType,
		isAllowFileSize,
		isAllowFileDuration,
		isAllowFileAspectRatio,
		fileName,
		isFileHasCopyString,
		customRequestUrl,
		isAllowVideoCodec,
	}: any) {
		let defaultErrorText = null;

		console.log('File validation for:', fileName);
		console.log('- isAllowedType:', isAllowedType);
		console.log('- isAllowFileSize:', isAllowFileSize);
		console.log('- isVideoFile:', isVideoFile);

		if (!customRequestUrl && !apiConfig?.requestUrl) {
			console.error('No valid URL provided for upload');
			return;
		}

		if (!isAllowedType) {
			console.error('File type not allowed:', fileName);
			return showUploadingError(index, allowedFileTypesErrorText);
		}

		if (!isAllowFileSize) {
			defaultErrorText = !isVideoFile
				? `Each file must not exceed ${maxFileSizeLabel}MB.`
				: 'Video file must not exceed 500MB.';
			console.error('File size exceeded:', fileName);
			return showUploadingError(
				index,
				allowedFileSizeErrorText ? allowedFileSizeErrorText : defaultErrorText,
			);
		}

		// Filename length is only validated when isFromEsign is false
		if (!isFromEsign && (!fileName || fileName.length < 3 || fileName.length > 64)) {
			defaultErrorText =
				'File name must be a minimum of 3 characters and not exceed 64 characters. Please check the file name and try again.';
			console.error('Filename length not valid:', fileName.length);
			return showUploadingError(index, defaultErrorText);
		}

		if (regexHasForbiddenChar.test(fileName)) {
			defaultErrorText =
				'File name contains characters that are not allowed. Please check the file name and try again.';
			console.error('Filename contains forbidden characters:', fileName);
			return showUploadingError(index, defaultErrorText);
		}

		if (!regexIsValidPath.test(fileName)) {
			defaultErrorText =
				'File name contains extra spaces that are not allowed. Please check the file name and try again.';
			console.error('Filename path not valid:', fileName);
			return showUploadingError(index, defaultErrorText);
		}

		if (isVideoFile) {
			if (!isAllowFileDuration) {
				defaultErrorText =
					'Video file must be between 3 seconds and 5 minutes.';
				console.error('Video duration not allowed:', fileName);
				return showUploadingError(index, defaultErrorText);
			}

			if (!isAllowFileAspectRatio) {
				defaultErrorText =
					'Video file must be between 1:1 and 16:9 aspect ratio.';
				console.error('Video aspect ratio not allowed:', fileName);
				return showUploadingError(index, defaultErrorText);
			}

			if (!isAllowVideoCodec) {
				defaultErrorText =
					'Video file has an unsupported codec. Please use H.264 (AVC) codec for video files.';
				console.error('Video codec not allowed:', fileName);
				return showUploadingError(index, defaultErrorText);
			}
		}

		if (isFileHasCopyString) {
			updateFileStatus(index, { progressValue: 0 });
		}

		const requestUrl = customRequestUrl || apiConfig.requestUrl;

		let requestData: any;
		let headers: any = {};

		if (isBulkUpload && bulkDocumentDetails?.length > 0) {
			requestData = {
				files: bulkDocumentDetails
			};
			headers = {
				'Content-Type': 'application/json'
			};
		} else {
			requestData = formData;
			headers = {
				'Content-Type': 'multipart/form-data',
			};
		}

		const additionalData = apiConfig?.additionalData;

		if (formData && additionalData && isListingBeta) {
			const { account_id, bay_access_token, listing_id, edit } = additionalData;

			if (account_id) formData.append('account_id', account_id);
			if (bay_access_token) formData.append('bay_access_token', bay_access_token);
			if (listing_id) formData.append('listing_id', listing_id);
			if (edit !== undefined) formData.append('edit', edit);
		}

		// Chunked upload: files larger than chunkSize are sent in sequential chunks
		const axiosInstance = apiConfig.axiosInstance || axios;
		const file = uploadingFiles[index];
		const isChunked = chunkSize > 0 && !isBulkUpload && file?.size > chunkSize;

		// Shared error handler for both upload paths
		const handleUploadError = (error: any) => {
			const isCancelled = error.code === 'ERR_CANCELED' || error.name === 'AbortError';

			// Always attempt server cleanup on cancel — file may have been saved before abort landed
			if (isCancelled && file?.name) {
				onCancelledUpload({ fileName: file.name });
			}

			updateFileStatus(index, {
				statusType: isCancelled ? 'cancel' : 'error',
				errorMessage: isCancelled
					? 'File upload was cancelled. Would you like to try again?'
					: error.message || 'There was an error uploading your file. Please try again.',
			});
			setFileUploadingStatus(index, isCancelled ? 'cancel' : 'error');
			setApiRes([]);
			throw error;
		};

		// Chunked upload path (files > chunkSize)
		if (isChunked) {
			const totalChunks = Math.ceil(file.size / chunkSize);
			const tempFolderUUID = uuidv4();
			let lastChunkData: any = null;
			try {
				for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
					if (modalHelper.current.fileProgressHelper[index]?.cancelled) {
						throw Object.assign(new Error('Upload cancelled'), { code: 'ERR_CANCELED' });
					}
					const start = chunkIndex * chunkSize;
					const end = Math.min(start + chunkSize, file.size);

					const chunkFormData = new FormData();
					chunkFormData.append('fileChunk', file.slice(start, end), file.name);
					chunkFormData.append('fileName', file.name);
					chunkFormData.append('chunkIndex', String(chunkIndex));
					chunkFormData.append('totalChunks', String(totalChunks));
					chunkFormData.append('tempFolderUUID', tempFolderUUID);

					const { data } = await axiosInstance({
						method: 'POST',
						url: requestUrl,
						data: chunkFormData,
						signal: getSignal(index),
						onUploadProgress: (e: any) => {
							if (e.lengthComputable) {
								const bytesUploadedTotal = start + e.loaded;
								const progress = Math.round((bytesUploadedTotal / file.size) * 100);
								updateFileStatus(index, { progressValue: progress, statusType: 'progress' });
								modalFooterRef.current?.setTime(0, index, bytesUploadedTotal, file.size);
							}
						},
					});

					if ((data.STATUS ?? data.status) === 1) {
						throw new Error(data.MESSAGE ?? data.message ?? 'Chunk upload failed');
					}
					lastChunkData = data;
				}
				updateFileStatus(index, { progressValue: 100, statusType: 'success' });
				setFileUploadingStatus(index, 'success');
				setApiRes(lastChunkData);
				return;
			} catch (error) {
				handleUploadError(error);
			}
		}

		// Normal upload path (files <= chunkSize or chunking disabled)
		try {
			if (isImageUploader && file) formData.append('file', file);

			const response = await axiosInstance({
				method: apiConfig.method ? apiConfig.method : 'POST',
				url: requestUrl,
				data: requestData,
				signal: getSignal(index),
				params: apiConfig.params ? apiConfig.params : {},
				headers,
				withCredentials: apiConfig.withCredentials
					? apiConfig.withCredentials
					: false,
				onUploadProgress: (progressEvent: any) => {
					const { loaded, total } = progressEvent;
					const startAt =
						modalHelper.current.fileProgressHelper[index].startAt;
					const progress = Math.round((100 * loaded) / total);
					updateFileStatus(index, {
						progressValue: progress,
						statusType: 'progress',
					});
					const timeElapsed = Date.now() - startAt;
					const uploadSpeed = loaded / timeElapsed;
					const duration = (total - loaded) / uploadSpeed;
					if (modalFooterRef.current) {
						modalFooterRef.current.setTime(duration, index, loaded, total);
					}
				},
			});

			if (response?.status == 200) {
				// File was cancelled but server processed it anyway - notify parent to delete it
				if (modalHelper.current.fileProgressHelper[index]?.cancelled) {
					onCancelledUpload(response?.data);
					setApiRes([]);
					return;
				}
				if (isUploadSection) {
					setApiRes(response);
				} else {
					setApiRes(response?.data);
				}
			} else {
				setApiRes([]);
			}
			updateFileStatus(index, {
				statusType: 'success',
			});
			setFileUploadingStatus(index, 'success');
			return response;
		} catch (error: any) {
			console.error('Upload error:', error);
			handleUploadError(error);
		}
	}

	function asyncUploadFunction(fileIndex: number) {
		return new Promise((resolve) => {
			setTimeout(() => {
				resolve(retryFileUpload(fileIndex));
			}, 2000);
		});
	}

	const processSyncUpload = async (syncFiles: number[], nextIndex?: number) => {
		const index = nextIndex ? nextIndex : 0;
		await asyncUploadFunction(syncFiles[index]);
		if (index < syncFiles.length - 1) {
			processSyncUpload(syncFiles, index + 1);
		}
	};

	const getFileName = (name: string) => {
		const fileNameWithoutExtension = name.substring(0, name.lastIndexOf('.'));
		return (
			(fileNameWithoutExtension || '')
				.split(' ')
				.filter((str) => str.trim() === 'copy') || []
		);
	};

	const isVideoFileCheck = (file: File) => {
		return isVideoFile(file);
	};

	const setFilesForUpload = async (
		acceptedFiles: File[],
		generateThumbnails = shouldGenerateThumbnails,
	) => {
		if (acceptedFiles?.length > 0 || (isBulkUpload && bulkDocumentDetails?.length > 0)) {
			const fileProgressHelper: any = {};
			const filesArray: any[] = [];
			const initialFileStatuses: any = {};
			const generatedThumbnails: any[] = [];
			modalHelper.current.fileProgressHelper = fileProgressHelper;

			const fileIds = (isBulkUpload && bulkDocumentDetails.length > 0)
				? bulkDocumentDetails.map(() => uuidv4())
				: acceptedFiles.map(() => uuidv4());

			if (isBulkUpload && bulkDocumentDetails.length > 0) {
				const sharedController = new AbortController();

				bulkDocumentDetails.forEach((fileDetail, index) => {
					fileProgressHelper[index] = {
						progress: 0,
						controller: sharedController,
						status: 'progress',
						startAt: Date.now(),
						cancelled: false,
						fileId: fileIds[index],
						isBulkUpload: true,
					};

					filesArray[index] = {
						filename: fileDetail.fileName,
						size: fileDetail.size || 0,
						type: getFileType(fileDetail.type) || 'pdf',
						lastupdated: dayjs(Date.now()).format('MMMM DD, YYYY [at] h:mm A'),
						id: index,
						file: null,
					};

					initialFileStatuses[index] = {
						progressValue: 0,
						statusType: 'progress',
						errorMessage: '',
					};
				});
			} else {
				if (
					generateThumbnails &&
					(!thumbnailFiles || thumbnailFiles.length === 0)
				) {
					for (let index = 0; index < acceptedFiles.length; index++) {
						const file = acceptedFiles[index];

						const controller = new AbortController();
						fileProgressHelper[index] = {
							progress: 0,
							controller,
							status: 'progress',
							startAt: Date.now(),
							cancelled: false,
							fileId: fileIds[index],
						};

						filesArray[index] = {
							filename: file.name,
							size: file.size,
							type: getFileType(file.type),
							lastupdated: dayjs(Date.now()).format('MMMM DD, YYYY [at] h:mm A'),
							id: acceptedFiles.length + index,
							file,
						};

						const isVideo = await isVideoFileCheck(file);

						if (isVideo) {
							if (file.size > VIDEO_MAX_SIZE_BYTES) {
								const errorMessage = 'Video file must not exceed 500MB.';
								showUploadingError(index, errorMessage);
								continue;
							}

							try {
								initialFileStatuses[index] = {
									progressValue: 0,
									statusType: 'processing',
									errorMessage: '',
								};

								try {
									const videoMetadata = await getVideoMetadata(file, isUploadSection);

									if (enforceVideoConstraints && videoMetadata) {
										if (
											videoMetadata.duration < VIDEO_MIN_DURATION_SEC ||
											videoMetadata.duration > VIDEO_MAX_DURATION_SEC
										) {
											const errorMessage =
												'Video file must be between 3 seconds and 5 minutes.';
											showUploadingError(index, errorMessage);
											continue;
										}

										if (
											videoMetadata.aspectRatio < VIDEO_MIN_ASPECT_RATIO ||
											videoMetadata.aspectRatio > VIDEO_MAX_ASPECT_RATIO
										) {
											const errorMessage =
												'Video file must be between 1:1 and 16:9 aspect ratio.';
											showUploadingError(index, errorMessage);
											continue;
										}
									}

									if (!videoMetadata.hasValidCodec) {
										const errorMessage =
											'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
										showUploadingError(index, errorMessage);
										continue;
									}
								} catch (metaError: any) {
									console.error(
										'Error getting video metadata during thumbnail setup:',
										metaError,
									);

									if (metaError.message && isDemuxerError(metaError.message)) {
										const errorMessage =
											'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
										showUploadingError(index, errorMessage);
										continue;
									}

									generatedThumbnails.push({
										fileIndex: index,
										thumbnailUrls: {
											default: FALLBACK_THUMBNAIL,
											custom: null,
										},
									});
									continue;
								}

								const thumbnailBase64 = await captureVideoThumbnail(file, 3, isUploadSection);

								if (thumbnailBase64 && !thumbnailBase64.startsWith('data:,')) {
									const uuid = uuidv4();
									const fileName = `${uuid}.jpg`;
									const thumbBlob = await fetch(thumbnailBase64).then((res) =>
										res.blob(),
									);
									const filesObject = new File([thumbBlob], fileName, {
										type: 'image/jpeg',
									});

									const formData = new FormData();
									formData.append('file', filesObject);

									try {
										const baseUrl = apiConfig.requestUrl.split('?')[0].split('/entries')[0].split('/partitions')[0];
										const thumbnailEndpoint = `${baseUrl}/partitions/${isUploadSection ? 'design-assets' : 'brand-thumbnails'}/entries$File?onConflict=rename&path=${encodeURIComponent(`default/${fileName}`)}`;

										const axiosInstance = apiConfig.axiosInstance || axios;
										const response = await axiosInstance({
											method: 'POST',
											url: thumbnailEndpoint,
											data: formData,
											headers: {
												'Content-Type': 'multipart/form-data',
											},
											withCredentials: apiConfig.withCredentials
												? apiConfig.withCredentials
												: false,
										});

										if (
											response.status === 200 &&
											response.data &&
											response.data.status === 0
										) {
											generatedThumbnails.push({
												fileIndex: index,
												thumbnailUrls: {
													default: response.data.result.url,
													custom: null,
												},
											});
										} else {
											console.error(
												'Error creating thumbnail url, status:',
												response.status,
												'data:',
												response.data,
											);
											generatedThumbnails.push({
												fileIndex: index,
												thumbnailUrls: {
													default: FALLBACK_THUMBNAIL,
													custom: null,
												},
											});
										}
									} catch (error) {
										console.error('Error uploading thumbnail:', error);
										generatedThumbnails.push({
											fileIndex: index,
											thumbnailUrls: {
												default: FALLBACK_THUMBNAIL,
												custom: null,
											},
										});
									}
								} else {
									console.error(
										'Invalid thumbnail generated or thumbnail generation failed',
									);
									generatedThumbnails.push({
										fileIndex: index,
										thumbnailUrls: {
											default: FALLBACK_THUMBNAIL,
											custom: null,
										},
									});
								}
							} catch (error: any) {
								console.error('Error generating thumbnail:', error);

								if (error.message && isDemuxerError(error.message)) {
									const errorMessage =
										'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
									showUploadingError(index, errorMessage);
									continue;
								}

								generatedThumbnails.push({
									fileIndex: index,
									thumbnailUrls: {
										default: FALLBACK_THUMBNAIL,
										custom: null,
									},
								});
							}
						}

						filesArray[index] = {
							filename: file.name,
							size: file.size,
							type: getFileType(file.type),
							lastupdated: dayjs(Date.now()).format('MMMM DD, YYYY [at] h:mm A'),
							id: acceptedFiles.length + index,
							file,
						};

						if (!initialFileStatuses[index]) {
							initialFileStatuses[index] = {
								progressValue: 0,
								statusType: 'progress',
								errorMessage: '',
							};
						}
					}
				} else {
					acceptedFiles.forEach((file, index) => {
						const controller = new AbortController();

						fileProgressHelper[index] = {
							progress: 0,
							controller,
							status: 'progress',
							startAt: Date.now(),
							cancelled: false,
							fileId: fileIds[index],
						};

						filesArray[index] = {
							filename: file.name,
							size: file.size,
							type: getFileType(file.type),
							lastupdated: dayjs(Date.now()).format('MMMM DD, YYYY [at] h:mm A'),
							id: acceptedFiles.length + index,
							file,
						};

						initialFileStatuses[index] = {
							progressValue: 0,
							statusType: 'progress',
							errorMessage: '',
						};
					});
				}
			}

			modalHelper.current = {
				fileProgressHelper,
				files: isBulkUpload ?
					bulkDocumentDetails.map((detail, index) => {
						const mockFile: any = {
							name: detail.fileName,
							size: detail.size || 0,
							type: getFileType(detail.type) || 'application/pdf',
							lastModified: detail.lastModified || Date.now(),
							webkitRelativePath: detail.directory || '',
							id: detail.id || index,
						};
						return mockFile;
					}) :
					acceptedFiles,
			};

			const mergedThumbnails = [
				...(thumbnailFiles || []),
				...generatedThumbnails,
			];

			setFileStatuses(initialFileStatuses);
			setProgressBarFiles(filesArray);
			setFileThumbnails(mergedThumbnails);

			setTimeout(() => {
				processUpload(isBulkUpload ? bulkDocumentDetails : acceptedFiles, mergedThumbnails);
			}, 500);
		}
	};

	const processUpload = (files: any, processThumbnailFiles: any = null) => {
		try {
			modalFooterRef.current?.setProcessing();
			const allowedFiles = getAllowedFileTypes(allowedFileTypes).types;

			if (isBulkUpload && bulkDocumentDetails.length > 0) {
				const totalFiles = bulkDocumentDetails.length;
				(async () => {
					try {
						const progressPerFile = 100 / totalFiles;

						for (let i = 0; i < totalFiles; i++) {
							updateFileStatus(i, {
								progressValue: (i + 1) * progressPerFile,
								statusType: 'progress',
							});

							await new Promise(resolve => setTimeout(resolve, 100));
						}

						await createFiles({
							formData: null,
							index: 0,
							isVideoFile: false,
							isAllowedType: true,
							isAllowFileSize: true,
							isAllowFileDuration: true,
							isAllowFileAspectRatio: true,
							fileName: 'bulk_upload',
							isFileHasCopyString: false,
							customRequestUrl: apiConfig.requestUrl,
							isAllowVideoCodec: true,
						});

						for (let i = 0; i < totalFiles; i++) {
							updateFileStatus(i, {
								progressValue: 100,
								statusType: 'success',
							});
							setFileUploadingStatus(i, 'success');
						}
					} catch (error) {
						console.error('Bulk upload error:', error);
						for (let i = 0; i < totalFiles; i++) {
							updateFileStatus(i, {
								progressValue: 100,
								statusType: 'error',
								errorMessage: 'Bulk upload failed. Please try again.',
							});
							setFileUploadingStatus(i, 'error');
						}
					}
				})();

				return;
			}

			const syncFiles: number[] = [];
			files.map((file: any, index: number) => {
				const isFileHasCopyString = getFileName(file.name).length > 0;
				if (isFileHasCopyString) {
					syncFiles.push(index);
				}
			});

			const regularFilePromises: any[] = [];

			for (let fileIndex = 0; fileIndex < files.length; fileIndex++) {
				const currentStatus =
					modalHelper.current.fileProgressHelper[fileIndex]?.status;
				if (currentStatus === 'invalid') {
					showUploadingError(
						fileIndex,
						modalHelper.current.fileProgressHelper[fileIndex]?.validationError,
					);
					continue;
				}

				if (syncFiles.includes(fileIndex)) continue;

				const uploadPromise = (async () => {
					const file = files[fileIndex];
					const isVideoMimeType = file.type.startsWith('video/');

					let videoCheck = false;
					if (isVideoMimeType) {
						try {
							videoCheck = await isVideoFileCheck(file);
						} catch (error) {
							console.error('Error checking if file is video:', error);
							videoCheck = false;
						}
					}

					console.log(`Processing file ${fileIndex}:`, file.name);
					console.log(
						`- Type: ${file.type}, Size: ${file.size}, Video: ${videoCheck}`,
					);

					let videoMetadata = null;
					let isAllowVideoCodec = true;

					if (videoCheck) {
						try {
							videoMetadata = await getVideoMetadata(file, isUploadSection);
							isAllowVideoCodec = videoMetadata?.hasValidCodec;
						} catch (error: any) {
							console.error('Error getting video metadata:', error);

							if (error.message && isDemuxerError(error.message)) {
								console.error(
									'Demuxer error detected in video file:',
									file.name,
								);
								const errorMessage =
									'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
								return showUploadingError(fileIndex, errorMessage);
							}

							isAllowVideoCodec = false;
						}
					}

					const fileType = getFileType(file.type);
					console.log(
						`- Detected file type: ${fileType}, Allowed types:`,
						allowedFiles,
					);

					let isAllowedType =
						allowedFiles.length === 0 ? true : allowedFiles.includes(fileType);

					if (!isAllowedType && file.type === 'image/jpeg') {
						isAllowedType =
							allowedFiles.includes('jpg') || allowedFiles.includes('jpeg');
					}

					console.log(`- File ${file.name} allowed: ${isAllowedType}`);

					const isAllowFileSize = !videoCheck
						? file.size < maxFileSize
						: file.size <= VIDEO_MAX_SIZE_BYTES;
					const isAllowFileDuration = videoCheck && enforceVideoConstraints
						? videoMetadata?.duration >= VIDEO_MIN_DURATION_SEC && videoMetadata?.duration <= VIDEO_MAX_DURATION_SEC
						: true;
					const isAllowFileAspectRatio = videoCheck && enforceVideoConstraints
						? videoMetadata?.aspectRatio >= VIDEO_MIN_ASPECT_RATIO &&
						videoMetadata?.aspectRatio <= VIDEO_MAX_ASPECT_RATIO
						: true;
					const isFileHasCopyString = getFileName(file.name).length > 0;

					const formData = new FormData();
					formData.append(formDataObjectName, file);

					let uploadUrl = apiConfig.requestUrl;
					const thumbnailsToUse =
						processThumbnailFiles ||
						(fileThumbnails.length > 0 ? fileThumbnails : thumbnailFiles);

					if (
						shouldGenerateThumbnails &&
						videoCheck &&
						thumbnailsToUse &&
						Array.isArray(thumbnailsToUse)
					) {
						const file = files[fileIndex];

						let thumbnail = thumbnailsToUse.find(
							(t: any) => t.fileIndex === fileIndex,
						);

						if (!thumbnail && file) {
							for (const t of thumbnailsToUse) {
								if (
									thumbnailsToUse.length === 1 ||
									(fileIndex < files.length && fileIndex >= 0)
								) {
									thumbnail = t;
									break;
								}
							}
						}

						if (thumbnail && thumbnail.thumbnailUrls) {
							uploadUrl += `${uploadUrl.includes('?') ? '&' : '?'}thumbnailUrls=${encodeURIComponent(
								JSON.stringify(thumbnail.thumbnailUrls),
							)}`;
						}
					}

					if (isUploadSection) {
						const timestamp = Date.now();
						const randomId = Math.random().toString(36).substring(2, 10);
						const actualFileName = file instanceof File ? (file.name.split('.').slice(0, -1).join('.')) : `file_${timestamp}`;
						const fileExtension = file instanceof File ? (file.name.split('.').pop() || 'png') : 'png';
						const fileName = `${actualFileName}-${timestamp}-${randomId}.${fileExtension}`;
						const additionalData = apiConfig?.additionalData;
						const { tempSessionId } = additionalData || {};
						const path =
							designId !== null ? `${designId}/${fileName}` : `temp/${tempSessionId}/${fileName}`;
						uploadUrl += `${uploadUrl.includes('?') ? '&' : '?'}path=${encodeURIComponent(path)}`;
					}

					try {
						await createFiles({
							formData,
							index: fileIndex,
							isVideoFile: videoCheck,
							isAllowedType,
							isAllowFileSize,
							isAllowFileDuration,
							isAllowFileAspectRatio,
							fileName: file.name,
							isFileHasCopyString,
							customRequestUrl: uploadUrl,
							isAllowVideoCodec,
						});
					} catch (error: any) {
						console.error(`Error uploading file at index ${fileIndex}:`, error);

						if (error.message && isDemuxerError(error.message)) {
							const errorMessage =
								'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
							showUploadingError(fileIndex, errorMessage);
						}
					}
				})();

				regularFilePromises.push(uploadPromise);
			}

			Promise.allSettled(regularFilePromises)
				.then(() => {
					if (syncFiles.length > 0) {
						processSyncUpload(syncFiles);
					} else {
						modalFooterRef.current?.setProcessing();
					}
				})
				.catch((error) => {
					console.error('Error in upload batch:', error);
					modalFooterRef.current?.setProcessing();
				});
		} catch (e) {
			console.error('Upload failed:', e);
			modalFooterRef.current?.setProcessing();
		}
	};

	const retryFileUpload = (index: number) => {
		const fileHelper = modalHelper.current.fileProgressHelper[index];
		const fileStatus = fileStatuses[index] || {};

		if (fileHelper && fileHelper.isBulkUpload) {
			console.warn('Retry not supported for bulk upload files');
			return;
		}

		if (fileStatus.statusType === 'invalid') {
			const error =
				(fileHelper && fileHelper.validationError) || fileStatus.errorMessage;

			if (error) {
				const preventRetryErrors = getPreventRetryErrors(enforceVideoConstraints);

				const shouldPreventRetry = preventRetryErrors.some((errorText) =>
					error.includes(errorText),
				);

				const isFileTypeError =
					error === allowedFileTypesErrorText ||
					error.includes('file type') ||
					error.includes('file format') ||
					error.includes('unsupported') ||
					error.includes('codec');

				if (shouldPreventRetry || isFileTypeError) {
					return;
				}
			}
		}

		setController(index);

		if (modalHelper.current.fileProgressHelper[index]) {
			modalHelper.current.fileProgressHelper[index].cancelled = false;
			modalHelper.current.fileProgressHelper[index].validationError = null;
		}

		setFileUploadingStatus(index, 'progress');
		updateFileStatus(index, {
			progressValue: 0,
			statusType: 'progress',
			errorMessage: '',
		});

		modalFooterRef.current?.setProcessing();

		const processRetry = async () => {
			try {
				const file = modalHelper.current.files[index];
				const allowedFiles = getAllowedFileTypes(allowedFileTypes).types;

				const isVideoMimeType = file.type.startsWith('video/');

				let videoCheck = false;
				if (isVideoMimeType) {
					try {
						videoCheck = await isVideoFileCheck(file);
					} catch (error) {
						console.error('Error checking if file is video:', error);
						videoCheck = false;
					}
				}
				let videoMetadata = null;
				let isAllowVideoCodec = true;

				if (videoCheck) {
					try {
						videoMetadata = await getVideoMetadata(file, isUploadSection);
						isAllowVideoCodec = videoMetadata?.hasValidCodec;
					} catch (error: any) {
						console.error('Error getting video metadata during retry:', error);

						if (error.message && isDemuxerError(error.message)) {
							const errorMessage =
								'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
							showUploadingError(index, errorMessage);
							return;
						}

						isAllowVideoCodec = false;
					}
				}

				const fileType = getFileType(file.type);
				console.log(
					`[Retry] Detected file type: ${fileType}, Allowed types:`,
					allowedFiles,
				);

				let isAllowedType =
					allowedFiles.length === 0 ? true : allowedFiles.includes(fileType);

				if (!isAllowedType && file.type === 'image/jpeg') {
					isAllowedType =
						allowedFiles.includes('jpg') || allowedFiles.includes('jpeg');
				}

				console.log(`[Retry] File ${file.name} allowed: ${isAllowedType}`);

				const isAllowFileSize = !videoCheck
					? file.size < maxFileSize
					: file.size <= VIDEO_MAX_SIZE_BYTES;
				const isAllowFileDuration = videoCheck && enforceVideoConstraints
					? videoMetadata?.duration >= VIDEO_MIN_DURATION_SEC && videoMetadata?.duration <= VIDEO_MAX_DURATION_SEC
					: true;
				const isAllowFileAspectRatio = videoCheck && enforceVideoConstraints
					? videoMetadata?.aspectRatio >= VIDEO_MIN_ASPECT_RATIO &&
					videoMetadata?.aspectRatio <= VIDEO_MAX_ASPECT_RATIO
					: true;

				const formData = new FormData();
				formData.append(formDataObjectName, file);

				let uploadUrl = apiConfig.requestUrl;
				const thumbnailsToUse =
					fileThumbnails.length > 0 ? fileThumbnails : thumbnailFiles;

				if (
					shouldGenerateThumbnails &&
					videoCheck &&
					thumbnailsToUse &&
					Array.isArray(thumbnailsToUse)
				) {
					let thumbnail = thumbnailsToUse.find((t: any) => t.fileIndex === index);

					if (!thumbnail && thumbnailsToUse.length === 1) {
						thumbnail = thumbnailsToUse[0];
					}

					if (thumbnail && thumbnail.thumbnailUrls) {
						uploadUrl += `${uploadUrl.includes('?') ? '&' : '?'}thumbnailUrls=${encodeURIComponent(
							JSON.stringify(thumbnail.thumbnailUrls),
						)}`;
					}
				}

				if (isUploadSection) {
					const timestamp = Date.now();
					const randomId = Math.random().toString(36).substring(2, 10);
					const actualFileName = file instanceof File ? (file.name.split('.').slice(0, -1).join('.')) : `file_${timestamp}`;
					const fileExtension = file instanceof File ? (file.name.split('.').pop() || 'png') : 'png';
					const fileName = `${actualFileName}-${timestamp}-${randomId}.${fileExtension}`;
					const additionalData = apiConfig?.additionalData;
					const { tempSessionId } = additionalData || {};
					const path =
						designId !== null ? `${designId}/${fileName}` : `temp/${tempSessionId}/${fileName}`;
					uploadUrl += `${uploadUrl.includes('?') ? '&' : '?'}path=${encodeURIComponent(path)}`;
				}

				const isFileHasCopyString = getFileName(file.name).length > 0;

				await createFiles({
					formData,
					index,
					isVideoFile: videoCheck,
					isAllowedType,
					isAllowFileSize,
					isAllowFileDuration,
					isAllowFileAspectRatio,
					fileName: file.name,
					isFileHasCopyString,
					customRequestUrl: uploadUrl,
					isAllowVideoCodec,
				});
			} catch (err: any) {
				if (err.message && isDemuxerError(err.message)) {
					const errorMessage =
						'Unsupported video format or codec. Please ensure the video uses H.264 (AVC) codec.';
					showUploadingError(index, errorMessage);
				} else {
					showUploadingError(
						index,
						err.message || 'Error retrying file upload.',
					);
				}
				console.error('Retry failed:', err);
			}
		};

		processRetry();
	};

	const cancelFileUpload = (index: number) => {
		abortController(index);
		onFileCancelled();
	};

	const showCancelAllFileUpload = () => {
		const inProgressFiles = Object.keys(
			modalHelper.current.fileProgressHelper,
		).filter(
			(index) =>
				modalHelper.current.fileProgressHelper[index].status === 'progress',
		);

		if (inProgressFiles.length === 0) {
			return;
		}

		cancelAllModalRef.current.setShowModal(true);
	};

	const cancelAllFileUpload = () => {
		const inProgressFiles = Object.keys(
			modalHelper.current.fileProgressHelper,
		).filter(
			(index) =>
				modalHelper.current.fileProgressHelper[index].status === 'progress',
		);

		inProgressFiles.forEach((index) => {
			if (modalHelper.current.fileProgressHelper[index]) {
				modalHelper.current.fileProgressHelper[index].cancelled = true;

				updateFileStatus(parseInt(index), {
					statusType: 'cancel',
					progressValue: 0,
					errorMessage:
						'File upload was cancelled. Would you like to try again?',
				});

				setFileUploadingStatus(parseInt(index), 'cancel');
			}
			abortController(parseInt(index));
		});

		if (inProgressFiles.length > 0) {
			onFileCancelled();
		}

		if (cancelAllModalRef.current) {
			cancelAllModalRef.current.setShowModal(false);
		}

		if (modalFooterRef.current) {
			modalFooterRef.current.setProcessing();
		}
	};

	const showCancelButton = () => {
		let showButton = false;
		const inProgressFiles = progressBarFiles.filter((file, index) => {
			return fileStatuses[index]?.statusType === 'progress';
		});
		if (inProgressFiles.length > 0) {
			showButton = true;
		}
		return showButton;
	};

	const renderUploadingFiles = () => {
		return progressBarFiles.map((file, index) => {
			const helperState = modalHelper.current.fileProgressHelper[index] || {};
			const reactState = fileStatuses[index] || {};

			const fileStatus: any = {
				progressValue: 0,
				statusType: 'progress',
				errorMessage: '',
			};

			if (helperState.status === 'progress' && !helperState.cancelled) {
				fileStatus.statusType = 'progress';
				fileStatus.progressValue = helperState.progress || 0;
				fileStatus.errorMessage = '';
			}
			else if (helperState.cancelled && reactState.statusType !== 'cancel') {
				fileStatus.statusType = 'cancel';
				fileStatus.progressValue = 0;
				fileStatus.errorMessage =
					'File upload was cancelled. Would you like to try again?';
			}
			else {
				fileStatus.progressValue =
					reactState.progressValue !== undefined
						? reactState.progressValue
						: helperState.progress || 0;
				fileStatus.statusType =
					reactState.statusType || helperState.status || 'progress';
				fileStatus.errorMessage =
					reactState.errorMessage || helperState.errorMessage || '';
			}

			let canRetry = true;

			if (helperState.isBulkUpload) {
				canRetry = false;
			}

			if (
				fileStatus.statusType === 'invalid' ||
				fileStatus.statusType === 'error'
			) {
				const error = fileStatus.errorMessage;

				if (error) {
					const preventRetryErrors = getPreventRetryErrors(enforceVideoConstraints);

					const shouldPreventRetry = preventRetryErrors.some((errorText: string) =>
						error.includes(errorText),
					);

					const isFileTypeError =
						error === allowedFileTypesErrorText ||
						error.includes('file type') ||
						error.includes('file format') ||
						error.includes('unsupported') ||
						error.includes('codec');

					if (shouldPreventRetry || isFileTypeError) {
						canRetry = false;
					}
				}
			}

			return (
				<div
					key={index}
					className={`d-flex justify-content-between py-4 border-gray-200 border border-top-0 border-end-0 border-start-0 ${index === progressBarFiles.length - 1 ? 'border-bottom-0' : ''} `}
				>
					<UploadProgressBar
						cancelFileUpload={cancelFileUpload}
						canRetry={canRetry}
						enableCancel={true}
						enforceVideoConstraints={enforceVideoConstraints}
						errorMessage={fileStatus.errorMessage}
						file={file}
						fileIndex={index}
						progressValue={fileStatus.progressValue}
						retryFileUpload={retryFileUpload}
						statusType={fileStatus.statusType}
						t={t}
					/>
				</div>
			);
		});
	};

	const closeUploadingModal = (reload = false) => {
		// Abort all in-progress uploads before closing
		const helpers = modalHelper.current.fileProgressHelper;
		const hasSuccessfulUploads = Object.values(helpers).some((h: any) => h?.status === 'success');
		const hadInProgress = Object.values(helpers).some((h: any) => h?.status === 'progress');
		Object.keys(helpers).forEach((index) => {
			const helper = helpers[index];
			if (helper?.status === 'progress' && helper.controller && !helper.controller.signal.aborted) {
				helper.cancelled = true;
				try { helper.controller.abort(); } catch (e) { /* ignore */ }
			}
		});

		if (hadInProgress) onFileCancelled();
		setProgressBarFiles([]);
		setFileStatuses({});
		setFileThumbnails([]);

		modalHelper.current = {
			fileProgressHelper: {},
			files: [],
		};

		handleModalClose({
			show: false,
			reload: reload || hasSuccessfulUploads || false,
		});
	};

	return (
		<>
			<Modal
				backdrop="static"
				backdropClassName="bay_modal_backdrop"
				centered={true}
				className="bay-files-progress-modal"
				show={showUploadingFilesModal}
				onHide={() => closeUploadingModal()}
			>
				<Modal.Header closeButton>
					<Modal.Title>
						{!progressBarFiles.length
							? 'Upload File'
							: `Uploading ${progressBarFiles.length} items`}
					</Modal.Title>
				</Modal.Header>
				<Modal.Body
					className={`bay-files-progress-modal-body ${!progressBarFiles.length && 'pb-5'}`}
				>
					{!progressBarFiles.length ? (
						<BayFileUpload
							allowedFileTypes={getAllowedFileTypes(allowedFileTypes).mimeTypes}
							allowedFileTypeslabel={allowedFileTypeslabel}
							allowedFileTypesText={allowedFileTypesText}
							allowedFileUpload={allowedFileUpload}
							cloudIconSize={cloudIconSize}
							noData={true}
							shouldGenerateThumbnails={shouldGenerateThumbnails}
							uploadLinkText={uploadLinkText}
							onDrop={onDrop}
						/>
					) : (
						renderUploadingFiles()
					)}
					{!progressBarFiles.length && copyrightDisclaimer && (
						<div className="upload-copyright-disclaimer d-flex mt-4">
							<BsShieldExclamation className="disclaimer-icon flex-shrink-0" size={14} />
							<span>
								{copyrightDisclaimer === true ? (
									<>
										{t('uploadCopyrightDisclaimer')}{' '}
										<a
											href="https://www.backatyou.com/tos"
											target="_blank"
											rel="noopener noreferrer"
										>
											{t('termsOfService')}
										</a>
										.
									</>
								) : (
									copyrightDisclaimer
								)}
							</span>
						</div>
					)}
				</Modal.Body>
				<UploadModalFooter
					ref={modalFooterRef}
					cancelAllFileUpload={() => showCancelAllFileUpload()}
					modalHelper={modalHelper}
					showCancelButton={showCancelButton()}
					t={t}
				/>
			</Modal>
			<CancelAllWarningModal
				ref={cancelAllModalRef}
				cancelAllFileUpload={() => cancelAllFileUpload()}
				t={t}
			/>
		</>
	);
};

export default BayFilesProgressModal;
